import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { confirmDelivery } from "../../../deliveries/redux/confirm/thunks/confirm-delivery";
import { initialState } from "./initial-state";
import { type DpiVerdict } from "./state";
import { listCaptures } from "./thunks/list-captures";
import { loadCustomer } from "./thunks/load-customer";
import { removeCapture } from "./thunks/remove-capture";
import { uploadCapture } from "./thunks/upload-capture";

const name = "kyc";

export const slice = createSlice({
  name,
  initialState,
  reducers: {
    dpiChecked: (state, action: PayloadAction<{ requestId: string; verdict: DpiVerdict }>) => {
      state.dpiCheck = action.payload;
    },
    dpiCheckReset: (state) => {
      state.dpiCheck = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCustomer.pending, (state) => {
        // Drop the previous expediente's customer while this one loads, so the
        // header never shows someone else's DPI to compare against.
        state.customerName = "";
        state.customerNationalId = "";
      })
      .addCase(loadCustomer.fulfilled, (state, action) => {
        state.customerName = action.payload.name;
        state.customerNationalId = action.payload.nationalId;
      })
      .addCase(listCaptures.pending, (state) => {
        state.isPending = true;
        state.isRejected = false;
        state.errorMessage = "";
      })
      .addCase(listCaptures.fulfilled, (state, action) => {
        state.captures = action.payload;
        state.isPending = false;
        state.isRejected = false;
      })
      .addCase(listCaptures.rejected, (state, action) => {
        state.isPending = false;
        state.isRejected = true;
        state.errorMessage = action.error.message ?? "";
      })
      .addCase(uploadCapture.pending, (state) => {
        state.isUploading = true;
        state.uploadError = "";
      })
      .addCase(uploadCapture.fulfilled, (state, action) => {
        state.isUploading = false;
        // Upsert by (type, page): a re-take replaces that page; a new contract
        // page appends without touching the others.
        const rest = state.captures.filter(
          (c) =>
            c.captureType !== action.payload.captureType ||
            c.page !== action.payload.page,
        );
        state.captures = [...rest, action.payload];
      })
      .addCase(uploadCapture.rejected, (state, action) => {
        state.isUploading = false;
        state.uploadError = action.error.message ?? "";
      })
      .addCase(removeCapture.fulfilled, (state, action) => {
        // Remove only the page that was deleted (contract pages are independent).
        state.captures = state.captures.filter(
          (c) =>
            c.captureType !== action.payload.type || c.page !== action.payload.page,
        );
      })
      // The delivery is done: this expediente's captures are no longer ours to
      // show. Dropping them here is what keeps the next expediente from opening
      // with the previous customer's checklist — a stale "done" row renders a
      // thumbnail under the NEW requestId, which 404s (DMS-61). We reset on
      // completion and not while a list is in flight on purpose: the ambassador
      // uploading evidence must keep seeing what they have already taken.
      .addCase(confirmDelivery.fulfilled, () => initialState);
  },
});

export const actions = slice.actions;
export const reducer = slice.reducer;
